import React, {Fragment, useCallback, useEffect, useRef, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {apiClient} from '@common/http/query-client';
import {useSettings} from '@common/core/settings/use-settings';
import {prettyBytes} from '@common/uploads/utils/pretty-bytes';
import {useAuth} from '@common/auth/use-auth';
import {ReceivePageHeader} from './receive-page-header';
import {downloadReceiveFile} from './send-e2ee-download';
// ─── Icons ────────────────────────────────────────────────────────────────────

function DownloadArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#4C4C4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReceiveFile {
  id: number;
  name: string;
  file_size: number;
  mime?: string;
  extension?: string;
  delivery_mode?: 'plain' | 'e2ee';
  e2ee_iv?: string | null;
  e2ee_wrapped_key?: string | null;
}

interface SenderInfo {
  name: string;
  email: string;
  avatar?: string | null;
}

interface SendPackagePayload {
  subject?: string | null;
  message?: string | null;
  files?: ReceiveFile[];
  created_at?: string | null;
  expires_at?: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}

function expiresIn(dateStr: string): string {
  const diff = Math.floor((new Date(dateStr).getTime() - Date.now()) / 1000);
  if (diff <= 0) return 'Expired';
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours`;
  return `${Math.floor(diff / 86400)} days`;
}

function fileExt(file: ReceiveFile): string {
  if (file.extension) return file.extension.toLowerCase();
  const parts = file.name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '—';
}

function PasswordSubmitIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[21px] w-[21px] shrink-0 text-white"
      fill="currentColor"
    >
      <path d="M8 5l10 7-10 7V5z" />
    </svg>
  );
}

interface ReceiveSenderSidebarProps {
  sender: SenderInfo | null;
  createdAt?: string | null;
  expiresAt?: string | null;
  isExpired?: boolean;
  isUnavailable?: boolean;
}

function ReceiveSenderSidebar({
  sender,
  createdAt,
  expiresAt,
  isExpired = false,
  isUnavailable = false,
}: ReceiveSenderSidebarProps) {
  return (
    <aside className="flex w-full shrink-0 flex-col items-center rounded-[60px] gap-[20px] border-2 border-[#E9ECF0] bg-[#E9ECF0] px-[28px] pb-[40px] pt-[64px] font-[Inter,sans-serif] lg:w-[396px] lg:min-h-[754px]">
      {sender?.avatar ? (
        <img
          src={sender.avatar}
          alt={sender.name}
          className="h-[160px] w-[160px] shrink-0 rounded-full object-cover"
        />
      ) : (
        <div className="flex h-[160px] w-[160px] shrink-0 items-center justify-center rounded-full bg-[#C8D4DE] text-[52px] font-bold text-[#5A7A96]">
          {sender?.name?.[0]?.toUpperCase() ?? '?'}
        </div>
      )}

      {/* Figma: John Doe — 32px / 700 / #535353 */}
      <p className="mt-[20px] min-h-[34px] text-center text-[32px] font-bold leading-normal text-[#535353]">
        {sender?.name ?? 'Unknown sender'}
      </p>

      {/* Figma: email — 16px / 700 / #535353, 184×18 */}
      <p className="mt-[4px] min-h-[18px] text-center text-[16px] font-bold leading-[18px] text-[#535353]">
        {sender?.email ?? ''}
      </p>

      {/* Figma: Rectangle 785 — 173×40, radius 24px, #FFF */}
      {createdAt && (
        <div className="mt-[20px] flex h-[40px] w-[173px] items-center justify-center rounded-[24px] bg-white">
          <span className="whitespace-nowrap text-[16px] font-bold leading-[18px] text-[#535353]">
            Shared {timeAgo(createdAt)}
          </span>
        </div>
      )}

      {isUnavailable ? (
        <div
          className={`flex h-[40px] w-[173px] items-center justify-center rounded-[24px] border-2 border-[#D64242] bg-white ${createdAt ? 'mt-[8px]' : 'mt-[20px]'}`}
        >
          <span className="whitespace-nowrap text-[16px] font-bold leading-[18px] text-[#D64242]">
            Not available
          </span>
        </div>
      ) : isExpired ? (
        <div
          className={`flex h-[40px] w-[173px] items-center justify-center rounded-[24px] border-2 border-[#D64242] bg-white ${createdAt ? 'mt-[8px]' : 'mt-[20px]'}`}
        >
          <span className="whitespace-nowrap text-[16px] font-bold leading-[18px] text-[#D64242]">
            Expired
          </span>
        </div>
      ) : (
        expiresAt && (
          <div
            className={`flex h-[40px] w-[173px] items-center justify-center rounded-[24px] bg-white ${createdAt ? 'mt-[8px]' : 'mt-[20px]'}`}
          >
            <span className="whitespace-nowrap text-[16px] font-bold leading-[18px] text-[#D64242]">
              Expires in: {expiresIn(expiresAt)}
            </span>
          </div>
        )
      )}
    </aside>
  );
}

function senderFirstName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'the sender';
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

interface ReceiveExpiredPanelProps {
  senderName: string;
}

function ReceiveExpiredPanel({senderName}: ReceiveExpiredPanelProps) {
  const firstName = senderFirstName(senderName);

  return (
    <div className="flex flex-1 flex-col items-center justify-center rounded-[60px] border-2 border-[#E9ECF0] bg-white px-[24px] font-[Inter,sans-serif] lg:min-h-[754px]">
      <h1 className="mx-auto max-w-full text-center text-[25px] font-bold leading-normal text-[#D8562A]">
        Files have expired.
      </h1>
      <p className="mx-auto mt-[12px] h-[25px] w-[401px] max-w-full whitespace-nowrap text-center text-[16px] font-bold leading-[25px] text-[#535353]">
        Oops! The files {firstName} shared are no longer accessible.
      </p>
    </div>
  );
}

interface ReceiveUnavailablePanelProps {
  senderName: string;
}

function ReceiveUnavailablePanel({senderName}: ReceiveUnavailablePanelProps) {
  const firstName = senderFirstName(senderName);

  return (
    <div className="flex flex-1 flex-col items-center justify-center rounded-[60px] border-2 border-[#E9ECF0] bg-white px-[24px] font-[Inter,sans-serif] lg:min-h-[754px]">
      <h1 className="mx-auto w-[305px] max-w-full text-center text-[25px] font-bold leading-normal text-[#D8562A]">
        Files no longer available.
      </h1>
      <p className="mx-auto mt-[12px] h-[25px] w-[387px] max-w-full whitespace-nowrap text-center text-[16px] font-bold leading-[25px] text-[#535353]">
        Oops! The files {firstName} shared are no longer available.
      </p>
    </div>
  );
}

interface ReceivePasswordPanelProps {
  password: string;
  onPasswordChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  error?: string;
}

function ReceivePasswordPanel({
  password,
  onPasswordChange,
  onSubmit,
  error,
}: ReceivePasswordPanelProps) {
  return (
    
    <div  className="relative flex flex-1 items-center justify-center rounded-[60px] border-2 border-[#E9ECF0] bg-white px-[24px] lg:min-h-[754px] max-sm:min-h-[400px] hero-mobile">
      {/* Headings sit above the input; input stays at vertical center of the white panel */}
      <div
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-center password-message"
        style={{bottom: 'calc(50% + 30px + 112px)'}}
      >
        <h1 className="mx-auto w-[383px] max-w-full text-[25px] font-bold leading-normal text-[#D8582A] heading">
          Files are password protected.
        </h1>
        <p className="mx-auto mt-[12px] h-[25px] w-[394px] max-w-full whitespace-nowrap text-[16px] font-bold leading-[25px] text-[#535353] paragraph">
          Enter password below to view and download files.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="relative z-10 flex w-full max-w-[370px] mt-[40px] flex-col items-center"
      >
        <div className="flex h-[60px] w-full max-w-[370px] items-stretch overflow-hidden rounded-[20px] bg-[#F1F1F1]">
          <input
            id="recv-password"
            type="password"
            value={password}
            onChange={e => onPasswordChange(e.target.value)}
            autoComplete="current-password"
            placeholder="Password"
            className="min-w-0 flex-1 border-0 bg-transparent px-[16px] text-[16px] font-normal leading-normal text-[#1A1A2E] outline-none placeholder:text-[#8B8B8B]"
          />
          <button
            type="submit"
            className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[20px] bg-[#2B7E20] p-0 transition-opacity hover:opacity-90"
            aria-label="Submit password"
          >
            <PasswordSubmitIcon />
          </button>
        </div>
        {error && (
          <p className="mt-[12px] w-full max-w-[370px] text-center text-[14px] font-medium text-[#D8582A]">
            {error}
          </p>
        )}
      </form>
    </div>
  );
}

function ReceiveTwoPanelLayout({
  sidebar,
  children,
}: {
  sidebar: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex w-full max-w-[1560px] flex-col gap-[17px] lg:flex-row lg:items-stretch">
      {sidebar}
      {children}
    </div>
  );
}

// ─── Fetch hook ───────────────────────────────────────────────────────────────

function useReceivePackage() {
  const {token} = useParams<{token: string}>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [passwordInput, setPasswordInput] = useState('');
  const [appliedPassword, setAppliedPassword] = useState(
    () => searchParams.get('password') || ''
  );
  const [pkg, setPkg] = useState<SendPackagePayload | null>(null);
  const [sender, setSender] = useState<SenderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);

  const fetchPackage = useCallback(
    async (password: string, updateUrl: boolean) => {
      if (!token) return;
      setLoading(true);
      setError('');
      setNeedsPassword(false);
      setIsExpired(false);
      setIsUnavailable(false);
      try {
        const params = password ? {password} : {};
        const {data} = await apiClient.get(`send-packages/${token}`, {params});
        setPkg((data?.sendPackage as SendPackagePayload) ?? null);
        setSender((data?.sender as SenderInfo) ?? null);
        if (updateUrl && password) setSearchParams({password});
      } catch (e: any) {
        const status = e?.response?.status;
        const body = e?.response?.data;
        if (body?.expired) {
          setIsExpired(true);
          setSender((body?.sender as SenderInfo) ?? null);
          setPkg((body?.sendPackage as SendPackagePayload) ?? null);
          setError('');
        } else if (body?.filesUnavailable) {
          setIsUnavailable(true);
          setSender((body?.sender as SenderInfo) ?? null);
          setPkg((body?.sendPackage as SendPackagePayload) ?? null);
          setError('');
        } else if (
          status === 403 &&
          (body?.passwordRequired || body?.passwordInvalid)
        ) {
          setNeedsPassword(true);
          setSender((body?.sender as SenderInfo) ?? null);
          setPkg((body?.sendPackage as SendPackagePayload) ?? null);
          setError(
            body?.passwordInvalid
              ? body?.message || 'Incorrect password. Please try again.'
              : ''
          );
        } else {
          setError(body?.message || 'This link is invalid or has expired.');
          setPkg(null);
          setSender(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [token, setSearchParams]
  );

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Missing send link.');
      return;
    }
    const fromUrl = searchParams.get('password') || '';
    setAppliedPassword(fromUrl);
    fetchPackage(fromUrl, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return {
    token,
    pkg,
    sender,
    loading,
    error,
    needsPassword,
    isExpired,
    isUnavailable,
    passwordInput,
    setPasswordInput,
    appliedPassword,
    setAppliedPassword,
    fetchPackage,
  };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SendReceivePage() {
  const state = useReceivePackage();
  const {isLoggedIn} = useAuth();
  const {base_url} = useSettings();

  // ── Selection state ──────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const files = state.pkg?.files ?? [];
  const allSelected = files.length > 0 && selectedIds.size === files.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(files.map(f => f.id)));
    }
  };

  const toggleFile = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Reset selection when package changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [state.pkg]);

  const downloadHref = (fileId: number) => {
    const q = new URLSearchParams();
    if (state.appliedPassword || state.passwordInput)
      q.set('password', state.appliedPassword || state.passwordInput);
    const suffix = q.toString() ? `?${q.toString()}` : '';
    return `${base_url}/api/v1/send-packages/${state.token}/files/${fileId}/download${suffix}`;
  };

  const onUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    const p = state.passwordInput.trim();
    state.setAppliedPassword(p);
    state.fetchPackage(p, true);
  };

  const hasMessage = !!(state.pkg?.subject || state.pkg?.message);

  // Files to act on: selected ones, or all if none selected
  const activeFiles =
    selectedIds.size > 0 ? files.filter(f => selectedIds.has(f.id)) : files;

  return (
    <Fragment>
      <DefaultMetaTags />
      <div className="min-h-screen bg-[#F5F5F7] flex flex-col dark:bg-[#14161a]">
        <ReceivePageHeader />

        <main className="flex-auto flex items-start justify-center px-[24px] pb-[40px] pt-[16px]">
          {state.loading && (
            <p className="mt-[120px] text-[15px] text-[#555] dark:text-[#b3bcc8]">
              Loading…
            </p>
          )}

          {!state.loading &&
            state.error &&
            !state.pkg &&
            !state.needsPassword &&
            !state.isExpired &&
            !state.isUnavailable && (
              <p className="mt-[120px] text-[15px] text-[#D8562A]">
                {state.error}
              </p>
            )}

          {/* Files removed / unavailable (Figma) */}
          {state.isUnavailable && !state.loading && (
            <ReceiveTwoPanelLayout
              sidebar={
                <ReceiveSenderSidebar
                  sender={state.sender}
                  createdAt={state.pkg?.created_at}
                  isUnavailable
                />
              }
            >
              <ReceiveUnavailablePanel
                senderName={state.sender?.name ?? 'Unknown sender'}
              />
            </ReceiveTwoPanelLayout>
          )}

          {/* Expired link (Figma: sender sidebar + expired message) */}
          {state.isExpired && !state.loading && (
            <ReceiveTwoPanelLayout
              sidebar={
                <ReceiveSenderSidebar
                  sender={state.sender}
                  createdAt={state.pkg?.created_at}
                  isExpired
                />
              }
            >
              <ReceiveExpiredPanel
                senderName={state.sender?.name ?? 'Unknown sender'}
              />
            </ReceiveTwoPanelLayout>
          )}

          {/* Password-protected (Figma: sender sidebar + password panel) */}
          {state.needsPassword && !state.loading && (
            <ReceiveTwoPanelLayout
              sidebar={
                <ReceiveSenderSidebar
                  sender={state.sender}
                  createdAt={state.pkg?.created_at}
                  expiresAt={state.pkg?.expires_at}
                />
              }
            >
              <ReceivePasswordPanel
                password={state.passwordInput}
                onPasswordChange={state.setPasswordInput}
                onSubmit={onUnlock}
                error={state.error}
              />
            </ReceiveTwoPanelLayout>
          )}

          {/* Two-panel layout — files unlocked */}
          {state.pkg &&
            !state.loading &&
            !state.needsPassword &&
            !state.isExpired &&
            !state.isUnavailable &&
            state.pkg.files?.length && (
              <ReceiveTwoPanelLayout
                sidebar={
                  <ReceiveSenderSidebar
                    sender={state.sender}
                    createdAt={state.pkg.created_at}
                    expiresAt={state.pkg.expires_at}
                  />
                }
              >
                <div
                  className="flex-1 rounded-[60px] border-2 border-[#E9ECF0] bg-white lg:min-h-[754px]"
                  style={{padding: '60px'}}
                >
                  {/* Header row */}
                  <div className="mb-[8px] flex items-center gap-[12px]">
                    {/* Bulk download — acts on selected files (or all if none selected) */}
                    <BulkDownloadButton
                      files={activeFiles}
                      downloadHref={downloadHref}
                      isLoggedIn={isLoggedIn}
                      token={state.token}
                    />
                    {/* Select-all circle: outlined when not all selected, filled green when all selected */}
                    <button
                      type="button"
                      onClick={toggleSelectAll}
                      className="shrink-0 transition-colors"
                      aria-label="Select all files"
                    >
                      <svg width="25" height="25" viewBox="0 0 25 25">
                        <circle
                          cx="12.5"
                          cy="12.5"
                          r="11.5"
                          fill={allSelected ? '#2B7E20' : '#FFFFFF'}
                          stroke="#2B7E20"
                          strokeWidth="2"
                        />
                      </svg>
                    </button>
                    {/* Label pill */}
                    <div
                      className="flex h-[45px] items-center rounded-[22px] border-2 border-[#E9ECF0] bg-[#E9ECF0] px-[22px] dark:border-[#2a2d33] dark:bg-[#1d2128]"
                      style={{width: '434px', flexShrink: 0}}
                    >
                      <span
                        className="text-[16px] font-medium leading-[19px] text-[#535353] dark:text-[#b3bcc8]"
                        style={{width: '174px', flexShrink: 0}}
                      >
                        Name
                      </span>
                      <span
                        className="text-[16px] font-medium leading-[19px] text-[#535353] dark:text-[#b3bcc8]"
                        style={{width: '92px', flexShrink: 0}}
                      >
                        File Type
                      </span>
                      <span className="flex-1" />
                      <span
                        className="text-[16px] font-medium leading-[19px] text-[#535353] dark:text-[#b3bcc8]"
                        style={{width: '61px', flexShrink: 0}}
                      >
                        Size
                      </span>
                      <span className="w-[30px] shrink-0" />
                    </div>
                  </div>

                  {/* File rows */}
                  <div
                    className="flex flex-col gap-[8px]"
                    style={{marginLeft: '109px'}}
                  >
                    {files.map(f => (
                      <FileRow
                        key={f.id}
                        file={f}
                        downloadHref={downloadHref(f.id)}
                        token={state.token}
                        isLoggedIn={isLoggedIn}
                        hasMessage={hasMessage}
                        subject={state.pkg!.subject}
                        message={state.pkg!.message}
                        isSelected={selectedIds.has(f.id)}
                        onToggleSelect={() => toggleFile(f.id)}
                      />
                    ))}
                  </div>
                </div>
              </ReceiveTwoPanelLayout>
            )}
        </main>
      </div>
    </Fragment>
  );
}

// ─── File row ─────────────────────────────────────────────────────────────────

interface FileRowProps {
  file: ReceiveFile;
  downloadHref: string;
  token: string | undefined;
  isLoggedIn: boolean;
  hasMessage: boolean;
  subject?: string | null;
  message?: string | null;
  isSelected: boolean;
  onToggleSelect: () => void;
}

function FileRow({
  file,
  downloadHref,
  token,
  isLoggedIn,
  hasMessage,
  subject,
  message,
  isSelected,
  onToggleSelect,
}: FileRowProps) {
  const [dlOpen, setDlOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [savedToDrive, setSavedToDrive] = useState(false);
  const [downloadingLocally, setDownloadingLocally] = useState(false);
  const dlRef = useRef<HTMLDivElement>(null);
  const msgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dlRef.current && !dlRef.current.contains(e.target as Node))
        setDlOpen(false);
      if (msgRef.current && !msgRef.current.contains(e.target as Node))
        setMsgOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const downloadLocally = async () => {
    setDownloadingLocally(true);
    try {
      await downloadReceiveFile(file, downloadHref);
    } catch {
      /* silent */
    } finally {
      setDownloadingLocally(false);
      setDlOpen(false);
    }
  };

  const saveToDrive = async () => {
    if (!token || savedToDrive) return;
    setSavingToDrive(true);
    try {
      await apiClient.post(
        `send-packages/${token}/files/${file.id}/save-to-drive`
      );
      setSavedToDrive(true);
    } catch {
      /* silent */
    } finally {
      setSavingToDrive(false);
      setDlOpen(false);
    }
  };

  return (
    /* Selected: border #2B7E20 | Unselected: border #E9ECF0 — border-radius 16px from Figma selected state */
    <div
      onClick={onToggleSelect}
      className={`flex h-[60px] w-full cursor-pointer items-center gap-[12px] rounded-[16px] border-2 bg-white px-[22px] mx-[-50px] transition-colors dark:bg-[#232832] ${
        isSelected
          ? 'border-[#2B7E20]'
          : 'border-[#E9ECF0] dark:border-[#2a2d33]'
      }`}
    >
      {/* File icon bg: 29×29, bg #090909 (dark) */}
      <div className="h-[29px] w-[29px] shrink-0 rounded-[4px] bg-[#D9D9D9]" />
      {/* Name: 174px wide, color #535353, 16px, weight 500 */}
      <span
        className="truncate text-[16px] font-medium leading-[19px] text-[#535353] dark:text-[#b3bcc8]"
        style={{width: '174px', flexShrink: 0}}
      >
        {file.name}
      </span>
      {/* File type: 92px wide, weight 500 */}
      <span
        className="text-[16px] font-medium leading-[19px] text-[#535353] dark:text-[#b3bcc8]"
        style={{width: '92px', flexShrink: 0}}
      >
        {fileExt(file)}
      </span>
      <span
        className="text-[16px] font-medium leading-[19px] text-[#535353] dark:text-[#b3bcc8]"
        style={{width: '61px', flexShrink: 0}}
      >
        {prettyBytes(file.file_size)}
      </span>
      <span className="flex-1" />
      {/* Actions — stop propagation so clicks here don't toggle row selection */}
      <div
        className="flex w-[30px] shrink-0 items-center justify-end gap-[6px]"
        onClick={e => e.stopPropagation()}
      >
        {/* Message icon */}
        {hasMessage && (
          <div className="relative" ref={msgRef}>
            <button
              type="button"
              onClick={() => {
                setMsgOpen(v => !v);
                setDlOpen(false);
              }}
              className="flex h-[28px] w-[28px] items-center justify-center rounded-full border-[#ccc] bg-white text-[#4C4C4C] hover:bg-[#F0F4F8] transition-colors dark:bg-[#232832] dark:text-[#f3f4f6] dark:hover:bg-[#2f3440]"
              aria-label="View message"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </button>
            {msgOpen && (
              <div className="absolute right-0 top-[36px] z-50 w-[260px] rounded-[20px] border-2 border-[#E9ECF0] bg-white p-[18px] shadow-[1px_1px_40px_1px_rgba(144,144,144,0.22)] dark:border-[#2a2d33] dark:bg-[#232832]">
                {subject && (
                  <div className="mb-[10px]">
                    <p className="mb-[3px] text-[11px] font-semibold uppercase tracking-wide text-[#888] dark:text-[#b3bcc8]">
                      Subject
                    </p>
                    <p className="text-[14px] text-[#1A1A2E] dark:text-[#f3f4f6]">
                      {subject}
                    </p>
                  </div>
                )}
                {message && (
                  <div>
                    <p className="mb-[3px] text-[11px] font-semibold uppercase tracking-wide text-[#888] dark:text-[#b3bcc8]">
                      Message
                    </p>
                    <p className="whitespace-pre-wrap text-[14px] text-[#1A1A2E] dark:text-[#f3f4f6]">
                      {message}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Download: logged-in gets Drive/Locally dropdown, guest gets direct link */}
        {isLoggedIn ? (
          <div className="relative" ref={dlRef}>
            <button
              type="button"
              onClick={() => {
                setDlOpen(v => !v);
                setMsgOpen(false);
              }}
              className="flex h-[30px] w-[30px] items-center justify-center rounded-full  border-[#ccc] bg-white hover:bg-[#F0F4F8] transition-colors dark:bg-[#232832] dark:text-[#f3f4f6] dark:hover:bg-[#2f3440]"
              aria-label={`Download ${file.name}`}
            >
              <DownloadArrowIcon />
            </button>
            {dlOpen && (
              <div className="absolute right-0 top-[38px] z-50 w-[179px] rounded-[24px] border-2 border-[#E9ECF0] bg-[#F8F9FF] py-[16px] shadow-[1px_1px_50px_1px_rgba(144,144,144,0.25)] dark:border-[#2a2d33] dark:bg-[#232832]">
                <button
                  type="button"
                  disabled={savingToDrive || savedToDrive}
                  onClick={saveToDrive}
                  className="block w-full px-[24px] py-[10px] text-left text-[17px] leading-[21px] text-[#1A1A2E] hover:bg-[#EEF2F7] transition-colors disabled:opacity-50 dark:text-[#f3f4f6] dark:hover:bg-[#2f3440]"
                >
                  {savingToDrive
                    ? 'Saving…'
                    : savedToDrive
                    ? 'Saved ✓'
                    : 'Drive'}
                </button>
                <button
                  type="button"
                  disabled={downloadingLocally}
                  onClick={downloadLocally}
                  className="block w-full px-[24px] py-[10px] text-left text-[17px] leading-[19px] text-[#1A1A2E] hover:bg-[#EEF2F7] transition-colors disabled:opacity-50 dark:text-[#f3f4f6] dark:hover:bg-[#2f3440]"
                >
                  {downloadingLocally ? 'Downloading…' : 'Locally'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            disabled={downloadingLocally}
            onClick={downloadLocally}
            className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-[#ccc] bg-white hover:bg-[#F0F4F8] transition-colors disabled:opacity-50 dark:bg-[#232832] dark:text-[#f3f4f6] dark:hover:bg-[#2f3440]"
            aria-label={`Download ${file.name}`}
          >
            <DownloadArrowIcon />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Bulk download ────────────────────────────────────────────────────────────

interface BulkDownloadButtonProps {
  files: ReceiveFile[];
  downloadHref: (id: number) => string;
  isLoggedIn: boolean;
  token: string | undefined;
}

function BulkDownloadButton({
  files,
  downloadHref,
  isLoggedIn,
  token,
}: BulkDownloadButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const downloadAllLocally = async () => {
    for (const f of files) {
      try {
        await downloadReceiveFile(f, downloadHref(f.id));
      } catch {
        /* silent */
      }
    }
    setOpen(false);
  };

  const saveAllToDrive = async () => {
    if (!token) return;
    for (const f of files) {
      try {
        await apiClient.post(
          `send-packages/${token}/files/${f.id}/save-to-drive`
        );
      } catch {
        /* silent */
      }
    }
    setOpen(false);
  };

  return (
    /* 60×60, border-radius 24px, border 2px #E9ECF0, bg #FFF */
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex h-[60px] w-[60px] items-center justify-center rounded-[24px] border-2 border-[#E9ECF0] bg-white text-[#4C4C4C] hover:bg-[#F0F4F8] transition-colors dark:border-[#2a2d33] dark:bg-[#232832] dark:text-[#f3f4f6] dark:hover:bg-[#2f3440]"
        aria-label="Download all"
      >
        <DownloadArrowIcon />
      </button>
      {open && (
        <div className="absolute left-0 top-[68px] z-50 w-[179px] rounded-[24px] border-2 border-[#E9ECF0] bg-[#F8F9FF] py-[16px] shadow-[1px_1px_50px_1px_rgba(144,144,144,0.25)] dark:border-[#2a2d33] dark:bg-[#232832]">
          {isLoggedIn && (
            <button
              type="button"
              onClick={saveAllToDrive}
              className="block w-full px-[24px] py-[10px] text-left text-[17px] leading-[21px] text-[#1A1A2E] hover:bg-[#EEF2F7] transition-colors dark:text-[#f3f4f6] dark:hover:bg-[#2f3440]"
            >
              Drive
            </button>
          )}
          <button
            type="button"
            onClick={downloadAllLocally}
            className="block w-full px-[24px] py-[10px] text-left text-[17px] leading-[19px] text-[#1A1A2E] hover:bg-[#EEF2F7] transition-colors dark:text-[#f3f4f6] dark:hover:bg-[#2f3440]"
          >
            Locally
          </button>
        </div>
      )}
    </div>
  );
}
